<script lang="ts" setup>
import { onMounted, ref, computed, markRaw, reactive } from "vue";
import { Close, DocumentAdd } from "@element-plus/icons-vue";
import { ElNotification, UploadProps } from "element-plus";
import type { UploadFile } from "element-plus";
import { ElementUIFormRef } from "~/types/element-ui/ElementUIFormRef";
import { FormRuleType } from "~/types/element-ui/FormRuleType";
import Helper from "~/composable/helper";
import { useCallHistoriesStore } from "~/projects/cc/stores/callHistorieStore";
import { useInboxStore } from "~/projects/help-desk/stores/inboxStore";
import { useConfigStore } from "~/stores/configStore";
import { PermissionNames } from "~/configs/permission_names";
import { useTasksStore } from "~/projects/task-manager/stores/tasksStore";
import { Task } from "~/projects/task-manager/types/task";
import { TaskPriority } from "~/projects/task-manager/types/enums/TaskPriority";
import { TaskStatus } from "~/projects/task-manager/types/enums/TaskStatus";
import useTaskDueDatePicker from "~/projects/task-manager/composables/useTaskDueDatePicker";
import { TaskableType } from "~/projects/task-manager/types/enums/TaskableType";

const configStore = useConfigStore();
const inboxStore = useInboxStore();
const callHistoriesStore = useCallHistoriesStore();
const tasksStore = useTasksStore();

const formRef = ref<ElementUIFormRef | null>(null);
const prop = defineProps({
  profileId: {
    type: Number,
    required: false,
    default: null,
  },
  showHeader: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(["after-task-created"]);
const taskForm = ref<Partial<Task>>({
  priority: TaskPriority.NORMAL,
  status: tasksStore.createTaskStatus,
  assigned_users: [{ id: null, type: undefined }],
});

const { toTitleCase } = Helper();
const { shortcuts, disabledDate } = useTaskDueDatePicker();

const fileList = ref<UploadFile[]>([]);

const showViewer = ref(false);
const previewIndex = ref(0);

const rules = reactive<FormRuleType>({
  title: [{ required: true, message: "Title is required", trigger: "blur" }],
  priority: [
    { required: true, message: "priority is required", trigger: "blur" },
  ],
  status: [{ required: true, message: "status is required", trigger: "blur" }],
  due_date: [
    { required: true, message: "due_date is required", trigger: "blur" },
  ],
  assigned_users: [
    { required: true, message: "assign a user", trigger: "blur" },
  ],
});
const createTask = function () {
  if (!formRef.value) {
    return;
  }
  formRef.value?.validate(async (valid: any) => {
    if (!valid) {
      ElNotification({
        title: "Failed",
        message: "Please provide all the required fields",
        type: "error",
      });
    } else if (valid) {
      const missingName = fileList.value.some(
        (item) => !item.attachmentName?.trim(),
      );
      if (missingName) {
        ElNotification({
          title: "Name is required",
          message: "Please provide a name for each attachment.",
          type: "error",
        });
        return;
      }
      let profileId;
      // profileId = inboxStore.selectedTicket.owner?.profile_id;
      if (
        tasksStore.taskableData?.taskable_type === TaskableType.call_history
      ) {
        profileId = tasksStore.taskProfileId;
      } else if (
        tasksStore.taskableData?.taskable_type === TaskableType.ticket
      ) {
        if (
          !inboxStore.selectedTicketProfile ||
          Object.keys(inboxStore.selectedTicketProfile).length === 0
        ) {
          await inboxStore.getSelectedTicketProfile(
            inboxStore.selectedTicket.owner?.profile_id,
          );
        }
        profileId = inboxStore.selectedTicketProfile?.id;
      }
      if (profileId) {
        taskForm.value.profile_id = profileId;
      }
      prop.profileId && (taskForm.value.profile_id = prop.profileId);
      const attachments = fileList.value.map((file) => ({
        file: file.raw,
        name: file.attachmentName,
        description: file.description || "",
      }));
      const task = await tasksStore.createTask(taskForm.value, attachments);
      if (!task.id) {
        ElNotification({
          title: "Failed",
          message: "Task creation failed",
          type: "error",
        });
        return;
      }
      emit("after-task-created");
      ElNotification({
        title: "task created",
        message: "new task created",
        type: "success",
      });

      tasksStore.setCreateModalVisibility(false);
      tasksStore.setCreateDialogVisibility(false);
      taskForm.value = {
        priority: TaskPriority.NORMAL,
        status: TaskStatus.OPEN,
      };
      fileList.value = [];

      await tasksStore.getTasks();
      await tasksStore.getStatusTask();
    }
  });
};

const closeCreateModal = async function () {
  formRef.value?.resetFields();
  fileList.value = [];
  tasksStore.setCreateModalVisibility(false);
  tasksStore.setCreateDialogVisibility(false);
};

const resetFields = () => {
  if (formRef.value) {
    formRef.value.resetFields();
  }
};
defineExpose({ resetFields });

const handleNotifyBefore = () => {
  taskForm.value.notify_before = taskForm.value.notify_before ? null : 10;
};
const handleDueDateChange = (value: string) => {
  if (value && !value.includes(" ")) {
    taskForm.value.due_date = `${value} 00:00:00`;
  }

  const [date, time] = taskForm.value.due_date.split(" ");
  taskForm.value.due_time = time || "00:00:00";

  handleNotifyBefore();
};
const handleUserSelection = () => {
  taskForm.value.assigned_users = taskForm.value.assigned_users?.map(
    (user) => ({
      id: user.id,
      type: user.type || undefined,
    }),
  );
};

const assignUser = () => {
  taskForm.value.assigned_users?.push({ id: null, type: undefined });
};
const removeUser = (index: number) => {
  taskForm.value.assigned_users?.splice(index, 1);
};
const handleFileChange: UploadProps["onChange"] = (uploadFile) => {
  const exists = fileList.value.find((file) => file.uid === uploadFile.uid);

  if (!uploadFile.url && uploadFile.raw?.type.startsWith("image")) {
    uploadFile.url = URL.createObjectURL(uploadFile.raw);
  }
  if (!uploadFile.attachmentName) {
    uploadFile.attachmentName = uploadFile.name
      .replace(/\.[^/.]+$/, "")
      .split(" ")[0];
  }

  if (!exists) {
    fileList.value.push({
      ...uploadFile,
      attachmentName: uploadFile.name,
      description: "",
    });
  }
};
const removeFile = (index: number) => {
  const file = fileList.value[index];
  if (file.url) {
    URL.revokeObjectURL(file.url);
  } else if (file.raw) {
    URL.revokeObjectURL(URL.createObjectURL(file.raw));
  }
  fileList.value.splice(index, 1);
};
const getAcceptValue = () => {
  const accept =
    ".jpg, .jpeg, .png, .pdf,.ppt, .pptx, .xlx,.xlxs, .doc, .docx, .wav, .heic, .mp3, .mp4";
  return accept;
};
const handlePreview: UploadProps["onPreview"] = (file) => {
  const exists = fileList.value.find((f) => f.uid === file.uid);
  if (!exists) {
    if (!file.url && file.raw?.type.startsWith("image")) {
      file.url = URL.createObjectURL(file.raw);
    }
    fileList.value.push(file);
  }
};
</script>
<template>
  <EditModalHeader v-if="prop.showHeader">
    <template #title> Add Tasks</template>
    <template #svg>
      <Svgs name="pen" />
    </template>
  </EditModalHeader>
  <el-form
    ref="formRef"
    :model="taskForm"
    :rules="rules as FormRuleType"
    @submit.prevent="createTask"
  >
    <div class="feature-filter-grid-1">
      <el-form-item class="" prop="title">
        <div class="form-elem-container">
          <FormLabel :required="true"> Title</FormLabel>
          <el-input v-model="taskForm.title" />
        </div>
      </el-form-item>

      <el-form-item class="" prop="status">
        <div class="form-elem-container">
          <FormLabel :required="true"> Status</FormLabel>
          <el-select v-model="taskForm.status">
            <el-option
              v-for="item in Object.entries(TaskStatus).map(([key, value]) => ({
                label: toTitleCase(value.replace('_', ' ')),
                value,
              }))"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </div>
      </el-form-item>
      <el-form-item class="form-elem-container" prop="category_id">
        <div class="form-elem-container">
          <FormLabel>Category</FormLabel>
          <el-select
            v-model="taskForm.category_id"
            filterable
            class="w-full"
            clearable
            placeholder="Please select category"
          >
            <el-option
              v-for="category in configStore.globalConfig.task_categories"
              :key="category.id"
              :label="category.name"
              :value="category.id"
            ></el-option>
          </el-select>
        </div>
      </el-form-item>

      <el-form-item class="" prop="priority">
        <div class="form-elem-container">
          <FormLabel :required="true"> Priority</FormLabel>
          <el-select v-model="taskForm.priority">
            <el-option
              v-for="[key, value] in Object.entries(TaskPriority).filter(
                ([key]) => isNaN(Number(key)),
              )"
              :key="value"
              :label="key"
              :value="value"
            />
          </el-select>
        </div>
      </el-form-item>

      <el-form-item class="" prop="assigned_users">
        <div class="form-elem-container">
          <FormLabel :required="true"> Assign Users</FormLabel>
          <div v-for="(_, index) in taskForm.assigned_users" :key="index">
            <div class="flex flex-col sm:flex-row items-center">
              <el-select
                v-model="taskForm.assigned_users[index].id"
                placeholder="Select User"
                filterable
                style="width: 100%"
                @change="handleUserSelection"
              >
                <el-option
                  v-for="agent in configStore.globalConfig.subordinates"
                  v-if="configStore.isOrgStructureHierarchical"
                  :key="agent.id"
                  :label="agent.name"
                  :value="agent.id"
                />
                <el-option
                  v-for="agent in configStore.globalConfig.agents"
                  v-if="!configStore.isOrgStructureHierarchical"
                  :key="agent.id"
                  :label="agent.name"
                  :value="agent.id"
                />
              </el-select>
              <el-select
                v-model="taskForm.assigned_users[index].type"
                placeholder="Select Type"
                clearable
                filterable
                style="width: 100%"
              >
                <el-option
                  v-for="type in configStore.globalConfig.task_manager
                    .task_users_association_types"
                  :key="type"
                  :label="type"
                  :value="type"
                />
              </el-select>
              <el-icon class="ml-2 cursor-pointer" @click="removeUser(index)">
                <Close
              /></el-icon>
            </div>
          </div>
        </div>
        <button
          class="cursor-pointer text-white bg-light-purple text-xs w-fit px-2 py-2 mt-2 rounded"
          :disabled="!isUserPermitted(PermissionNames.TASK_ASSIGN_USER)"
          :title="
            !isUserPermitted(PermissionNames.TASK_ASSIGN_USER)
              ? 'You don\'t have permission'
              : ''
          "
          @click.prevent="assignUser"
        >
          <span> Assign User </span>
        </button>
      </el-form-item>

      <el-form-item class="" prop="due_date">
        <div class="form-elem-container">
          <FormLabel :required="true"> Due Date & Time</FormLabel>
          <el-date-picker
            v-model="taskForm.due_date"
            placeholder="Select date & time"
            type="datetime"
            format="YYYY-MM-DD HH:mm:ss"
            value-format="YYYY-MM-DD HH:mm:ss"
            :shortcuts="shortcuts"
            :disabled-date="disabledDate"
            @change="handleDueDateChange"
          />
        </div>
      </el-form-item>

      <el-form-item class="" prop="notify_before">
        <div class="form-elem-container">
          <FormLabel> Notify Before (in multiple of 10 minutes)</FormLabel>
          <el-input-number
            v-model="taskForm.notify_before"
            :step="10"
            step-strictly
            controls-position="right"
            :min="10"
            style="width: 100%"
            :disabled="!taskForm.due_time"
            :placeholder="!taskForm.due_time ? 'Select due time first' : ''"
          />
        </div>
      </el-form-item>

      <el-form-item class="" prop="description">
        <div class="form-elem-container">
          <FormLabel>Description</FormLabel>
          <el-input
            v-model="taskForm.description"
            type="textarea"
            rows="5"
            class="w-full shadow"
            placeholder="Place a more detailed description"
          />
        </div>
      </el-form-item>
      <el-form-item prop="attachments">
        <div class="flex flex-col gap-4">
          <div class="flex flex-col md:flex-row md:items-center gap-2">
            <FormLabel>Attachments</FormLabel>
            <div class="ml-2 mb-2">
              <el-button
                size="small"
                plain
                @click="$refs.uploadRef?.$el.querySelector('input').click()"
              >
                <DocumentAdd class="w-4 h-4" />
                <p>Add Attachments</p>
              </el-button>
            </div>
          </div>

          <el-upload
            ref="uploadRef"
            v-model:file-list="fileList"
            :auto-upload="false"
            :multiple="true"
            :show-file-list="false"
            :on-preview="handlePreview"
            :on-change="handleFileChange"
            :accept="getAcceptValue()"
            style="display: none"
          />

          <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div
              v-for="(file, index) in fileList"
              :key="file.uid"
              class="flex items-center gap-4 border p-2 rounded-md"
            >
              <div
                class="w-16 h-16 flex items-center justify-center bg-gray-100 rounded"
              >
                <img
                  v-if="file.url || file.raw?.type.startsWith('image')"
                  :src="file.url"
                  class="w-full h-full object-cover rounded cursor-pointer"
                  @click="
                    () => {
                      previewIndex = index;
                      showViewer = true;
                    }
                  "
                />
                <el-icon v-else-if="file.raw?.type?.startsWith('audio')"
                  ><Headset
                /></el-icon>
                <el-icon v-else-if="file.raw?.type?.startsWith('video')"
                  ><VideoCamera
                /></el-icon>
                <el-icon v-else-if="file.raw?.type === 'application/pdf'"
                  ><Document
                /></el-icon>
                <el-icon v-else><Folder /></el-icon>
              </div>

              <div class="flex flex-col gap-1">
                <el-input
                  v-model="file.attachmentName"
                  placeholder="File name"
                  class="w-1/3"
                />
                <el-input
                  v-model="file.description"
                  placeholder="File description"
                  type="textarea"
                />
              </div>

              <el-button circle size="small" @click="removeFile(index)">
                <el-icon><Close /></el-icon>
              </el-button>
            </div>
          </div>
        </div>
      </el-form-item>
    </div>
    <div class="feature-filter-submit">
      <NavButton type="button" @click="closeCreateModal"> Cancel</NavButton>
      <NavButton
        class="bg-light-purple-nav"
        type="submit"
        :is-disabled="!isUserPermitted(PermissionNames.TASK_CREATE)"
      >
        Create
      </NavButton>
    </div>
  </el-form>
</template>
